import React from 'react';
import {
  Breakpoints,
  Checkbox,
  Fieldset,
  styled,
  Text,
  TokenTextAppearance,
  TextTag
} from '@volkswagen-onehub/components-core';
import { getTranslations } from '../../translations';
import { useFeatureServices } from '../../use-feature-services';
import { createConfiguration, createConstantData, createPartner } from '../../utils/get-tracking-event-data';
import { getSingleSelectedDealer } from '../../utils/get-single-selected-dealer';
import { useSelectedFilterStore } from '../../store/filtersv2';

type Props = {
  initialSearch?: boolean;
  hideLegend?: boolean;
};

const FieldsetWrapper = styled.div<Props>`
  width: 100%;
  max-width: ${({initialSearch}) => initialSearch ? '60%': 'unset'};
  margin: ${({initialSearch}) => initialSearch ? '0 auto': ''};

  > * {
    margin: 0 auto;
    width: ${({initialSearch}) => initialSearch ? 'min-content' : 'auto'};
    @media (min-width: ${Breakpoints.b560}px) {
      width: auto;
    }
  }
  legend {
    font-size: ${props => props.theme.fonts.bigcopy[100].fontSize};
    line-height: ${props => props.theme.fonts.bigcopy[100].lineHeight};
    letter-spacing: ${props => props.theme.fonts.bigcopy[100].letterSpacing};
    color: #001E50 !important;
  }
  [aria-live] {
    margin: 0;
  }
  span[class^=StyledFocusWrapper] {
    margin: 12px 0 0;
  }
  span[class^=StyledTextComponent] {
    width: max-content;
    display: block;
  }
`;

const CheckboxContainer = styled.div<Props>`
  width: 100%;
  margin: 0 ${({initialSearch}) => initialSearch ? 'auto': ''};
  display: flex;
  flex-direction: column;
  column-gap: 32px;
  flex-wrap: wrap;
  align-items: flex-start;

  @media screen and (min-width: ${Breakpoints.b560}px) {
    flex-wrap: nowrap;
  }
`;

export const InventoryType = ({ initialSearch }: Props) => {
  const { labels, volksklick } = getTranslations();

  const { selected, selectFilter } = useSelectedFilterStore();
  const { category: selectedCategories } = selected;

  const { tracking } = useFeatureServices();
  const partner = getSingleSelectedDealer();

  const handleClick = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const type = e.currentTarget.value;
    let updatedSelection = selectedCategories;

    if (type === "certified" && !selectedCategories?.includes("certified") && selectedCategories?.includes("used") ) {
      updatedSelection = [...selectedCategories.filter((item) => item !== "used"), type]
    } else if (type === "used" && !selectedCategories?.includes("used") && selectedCategories?.includes("certified")) {
      updatedSelection = [...selectedCategories.filter((item) => item !== "certified"), type]
    } else if (selectedCategories?.includes(type)) {
      updatedSelection = selectedCategories.filter((item) => item !== type);
    } else if (selectedCategories) {
      updatedSelection = [...selectedCategories, type];
    } else {
      updatedSelection = [type];
    }

    selectFilter("category", updatedSelection);

    if (e.currentTarget.checked) {
      tracking.track("VWBasic_FilterSelect_Click", {
        ...createConstantData(),
        configuration: createConfiguration(),
        partner: createPartner(partner),
        link: {
          url: window.location.href,
          name: (e.currentTarget.offsetParent as HTMLLabelElement)?.innerText
        },
        filter: {
          type: 'multi_value',
          name: labels.inventory_type,
          values: updatedSelection,
        }
      });
    }
  };

  const getFieldSetCopy = () => {
    return initialSearch ? volksklick.searchFor : undefined;
  };

  return (
    <FieldsetWrapper initialSearch={initialSearch}>
      <Fieldset legend={getFieldSetCopy()}>
        <CheckboxContainer initialSearch={initialSearch}>
          <Checkbox
            key="new"
            value="new"
            name="category"
            label={
              <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
              { labels.new }
              </Text>
            }
            checked={selectedCategories?.includes("new")}
            onClick={(e) => handleClick(e)}
          />
          <Checkbox
            key="used"
            value="used"
            name="category"
            label={
              <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
                { labels.used }
              </Text>
            }
            checked={selectedCategories?.includes("used")}
            onClick={(e) => handleClick(e)}
          />
          <Checkbox
            key="certified"
            value="certified"
            name="category"
            label={
              <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
                { labels.certified }
              </Text>
            }
            checked={selectedCategories?.includes("certified")}
            onClick={(e) => handleClick(e)}
          />
          <Checkbox
            key="demo"
            value="demo"
            name="category"
            label={
              <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
                { labels.demo }
              </Text>
            }
            checked={selectedCategories?.includes("demo")}
            onClick={(e) => handleClick(e)}
          />
        </CheckboxContainer>
      </Fieldset>
    </FieldsetWrapper>
  );
};
